import {
    StrictEventEmitter,
    EventMap,
    EventListenerFn,
    EventAllListenerFn,
    EventOneOfListenerFn,
    FunctionArgsTernary,
    EventArg
} from './StrictEventEmitter';

export class StrictEventEmitterWrapper<T extends EventMap> {
    constructor(readonly emitter: StrictEventEmitter<T>, readonly signal: AbortSignal) {}

    on<K extends keyof T>(eventName: K, cb: EventListenerFn<T, K>): () => void {
        return this.emitter.on(eventName, cb, { signal: this.signal });
    }

    once<K extends keyof T>(eventName: K, cb: EventListenerFn<T, K>): () => void {
        return this.emitter.once(eventName, cb, { signal: this.signal });
    }

    onceAll<K extends keyof T, P extends K[]>(eventNames: P, cb: EventAllListenerFn<T, P[number]>): () => void {
        return this.emitter.onceAll(eventNames, cb, { signal: this.signal });
    }

    onceOneOf<K extends keyof T, P extends K[]>(eventNames: P, cb: EventOneOfListenerFn<T, P[number]>): () => void {
        return this.emitter.onceOneOf(eventNames, cb, { signal: this.signal });
    }

    emit<K extends keyof T, V extends T[K]>(...args: FunctionArgsTernary<V, [key: K, val: V], [key: K]>): void {
        return this.emitter.emit(...args);
    }

    onceAsync<K extends keyof T>(eventName: K): Promise<EventArg<T, K>> {
        return this.emitter.onceAsync(eventName, { signal: this.signal });
    }

    onceAllAsync<K extends keyof T, P extends K[]>(eventNames: P): Promise<{ [P in K]: EventArg<T, P> }> {
        return this.emitter.onceAllAsync(eventNames, { signal: this.signal });
    }

    onceOneOfAsync<K extends keyof T, P extends K[]>(eventNames: P): Promise<{ [P in K]?: EventArg<T, P> }> {
        return this.emitter.onceOneOfAsync(eventNames, { signal: this.signal });
    }

    async emitAsync<K extends keyof T, V extends T[K]>(
        ...args: FunctionArgsTernary<V, [key: K, val: V], [key: K, val?: undefined]>
    ): Promise<void> {
        return this.emitter.emitAsync(...args);
    }

    off<K extends keyof T>(eventName: K, cb: EventListenerFn<T, K>): void {
        return this.emitter.off(eventName, cb);
    }

    offByOwner<K extends keyof T>(eventName: K, owner: object): void {
        return this.emitter.offByOwner(eventName, owner);
    }

    offAllByOwner(owner: object): void {
        return this.emitter.offAllByOwner(owner);
    }
}
